import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { updateTeacherProfile } from "@/api/teachers";
import type { Teacher } from "@/types/teacher";

interface Props {
  open: boolean;
  teacher: Teacher;
  onClose: () => void;
}

export function TeacherProfileDialog({ open, teacher, onClose }: Props) {
  const queryClient = useQueryClient();
  const [birthYear, setBirthYear] = useState("");
  const [experience, setExperience] = useState("");
  const [education, setEducation] = useState("");
  const [langs, setLangs] = useState("");
  const [bio, setBio] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setBirthYear(teacher.birthYear ? String(teacher.birthYear) : "");
    setExperience(teacher.experience ? String(teacher.experience) : "");
    setEducation(teacher.education ?? "");
    setLangs((teacher.langs ?? []).join(", "));
    setBio(teacher.bio ?? "");
    setError(null);
  }, [open, teacher]);

  const save = useMutation({
    mutationFn: () =>
      updateTeacherProfile(teacher.id, {
        birthYear: birthYear ? Number(birthYear) : null,
        experience: experience ? Number(experience) : null,
        education: education.trim() || null,
        langs: langs
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean),
        bio: bio.trim() || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["teacher", teacher.id] });
      await queryClient.invalidateQueries({ queryKey: ["teachers"] });
      onClose();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Не удалось сохранить профиль"),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Профиль · {teacher.fullName}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={0.5}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Год рождения"
              type="number"
              size="small"
              fullWidth
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
            />
            <TextField
              label="Опыт работы, лет"
              type="number"
              size="small"
              fullWidth
              value={experience}
              onChange={(e) => setExperience(e.target.value)}
            />
          </Stack>
          <TextField
            label="Образование"
            size="small"
            fullWidth
            placeholder="Вуз, факультет"
            value={education}
            onChange={(e) => setEducation(e.target.value)}
          />
          <TextField
            label="Языки преподавания"
            size="small"
            fullWidth
            helperText="Через запятую"
            value={langs}
            onChange={(e) => setLangs(e.target.value)}
          />
          <TextField
            label="О себе"
            size="small"
            fullWidth
            multiline
            minRows={4}
            helperText="Текст виден родителям учеников в Telegram"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
